// src/pages/general/GeneralSkillDetails.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  BookOpen,
  Loader2,
  ArrowRight,
  Trash2,
  BookText,
  Headphones,
  Mic,
  PenLine,
  AlignLeft,
  Hash,
  Pencil,
  CheckCircle2,
  X,
  ChevronDown,
  ChevronUp,
  Check,
  ChevronRight,
  GitBranch,
  Video,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
  Repeat,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import {
  generalSkillsAPI,
  generalQuestionsAPI,
} from "../../services/generalService";
import TranslationField from "../../components/TranslationField";

const SKILL_TYPE_LABELS = {
  VOCABULARY: "Vocabulary",
  GRAMMAR: "Grammar",
  READING: "Reading",
  LISTENING: "Listening",
  SPEAKING: "Speaking",
  WRITING: "Writing",
  GENERAL_PATH: "General Path",
};

const SKILL_ICONS = {
  VOCABULARY: Hash,
  GRAMMAR: AlignLeft,
  READING: BookText,
  LISTENING: Headphones,
  SPEAKING: Mic,
  WRITING: PenLine,
  GENERAL_PATH: GitBranch,
};

const DIFFICULTY_COLORS = {
  EASY: "bg-emerald-100 text-emerald-700",
  MEDIUM: "bg-amber-100 text-amber-700",
  HARD: "bg-red-100 text-red-700",
};

// ─────────────────────────────────────────────
// أنواع الأسئلة اللي ممكن نحول لها في GENERAL_PATH
// (WRITING مستبعدة عمدًا — مش موجودة كـ section في GENERAL_PATH أصلاً)
// ─────────────────────────────────────────────
const MCQ_TYPE_OPTIONS = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING", "SPEAKING"];
const PARENT_REQUIRED_TYPES = ["READING", "LISTENING", "SPEAKING"];
const TYPE_LABELS_AR = {
  VOCABULARY: "مفردات",
  GRAMMAR: "قواعد",
  READING: "قراءة",
  LISTENING: "استماع",
  SPEAKING: "تحدث",
};

// ─────────────────────────────────────────────
// Rich text editor for the question text (Bold / Italic / Underline / Highlight)
// ─────────────────────────────────────────────
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function RichTextEditor({
  value,
  onChange,
  accentColor = "emerald",
  dir = "rtl",
  minHeight = "90px",
  compact = false,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none min-h-[${minHeight}] px-3 ${
          compact ? "py-1.5" : "py-2"
        } focus:outline-none ${dir === "rtl" ? "text-right" : "text-left"}`,
        dir,
      },
    },
  });

  if (!editor) return null;

  return (
    <div
      className={`border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-${accentColor}-500 bg-white`}
    >
      <div
        className={`flex gap-1 border-b border-gray-200 bg-gray-50 rounded-t-xl ${
          compact ? "p-0.5" : "p-1"
        }`}
      >
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/** Strips HTML tags to check whether an editor's content is effectively empty */
const isHtmlEmpty = (html) =>
  !html || html.replace(/<[^>]*>/g, "").trim() === "";

// ─────────────────────────────────────────────
// Rendered (read-only) rich text
// Used for: question_text, choices, explanations — anything that comes
// back from the API as HTML. Always render HTML fields through this
// component (dangerouslySetInnerHTML), never as plain `{value}`.
// ─────────────────────────────────────────────
function RichText({ html, className = "", dir = "rtl" }) {
  if (!html) return null;
  return (
    <div
      dir={dir}
      className={`prose prose-sm max-w-none ${
        dir === "rtl" ? "text-right" : "text-left"
      } ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ─────────────────────────────────────────────
// Reusable Image Field (upload / preview / remove)
// ─────────────────────────────────────────────
function ImageField({ preview, onChange, onRemove, accentColor = "emerald" }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 mb-1 block text-right">
        الصورة (اختياري)
      </label>
      {preview ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="w-full max-h-40 object-contain rounded-lg border border-gray-200"
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label
          className={`flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-5 cursor-pointer hover:border-${accentColor}-400 transition-colors`}
        >
          <ImageIcon className="w-6 h-6 text-gray-400 mb-1" />
          <span className="text-xs text-gray-500">اضغط لرفع صورة</span>
          <input
            type="file"
            accept="image/*"
            onChange={onChange}
            className="hidden"
          />
        </label>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// General Path Sections Config
// ─────────────────────────────────────────────
const GENERAL_PATH_SECTIONS = [
  {
    type: "VOCABULARY",
    label: "Vocabulary",
    icon: Hash,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    headerBg: "bg-blue-50",
    btnColor: "bg-blue-500 hover:bg-blue-600",
    addLabel: "Add Vocabulary Question",
    addRoute: (skillId) =>
      `/dashboard/general/skills/${skillId}/add/vocabulary`,
  },
  {
    type: "GRAMMAR",
    label: "Grammar",
    icon: AlignLeft,
    color: "text-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    headerBg: "bg-purple-50",
    btnColor: "bg-purple-500 hover:bg-purple-600",
    addLabel: "Add Grammar Question",
    addRoute: (skillId) => `/dashboard/general/skills/${skillId}/add/grammar`,
  },
  {
    type: "READING",
    label: "Reading",
    icon: BookText,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    headerBg: "bg-orange-50",
    btnColor: "bg-orange-500 hover:bg-orange-600",
    addLabel: "Add Reading Passage",
    addRoute: (skillId) =>
      `/dashboard/general/skills/${skillId}/add/reading/passage`,
  },
  {
    type: "LISTENING",
    label: "Listening",
    icon: Headphones,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    headerBg: "bg-cyan-50",
    btnColor: "bg-cyan-500 hover:bg-cyan-600",
    addLabel: "Add Listening Recording",
    addRoute: (skillId) =>
      `/dashboard/general/skills/${skillId}/add/listening/audio`,
  },
  {
    type: "SPEAKING",
    label: "Speaking",
    icon: Video,
    color: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
    headerBg: "bg-rose-50",
    btnColor: "bg-rose-500 hover:bg-rose-600",
    addLabel: "Add Speaking Video",
    addRoute: (skillId) =>
      `/dashboard/general/skills/${skillId}/add/speaking/video`,
  },
];

// ─────────────────────────────────────────────
// MCQ Card — shared for Vocabulary, Grammar,
//            Reading questions, Listening questions, Speaking questions
// ─────────────────────────────────────────────
function MCQCard({ q, type, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const choices = [
    { key: "A", value: q.choice_a },
    { key: "B", value: q.choice_b },
    { key: "C", value: q.choice_c },
    { key: "D", value: q.choice_d },
  ].filter((c) => c.value);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Question header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Actions */}
          <div className="flex flex-col gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit({ q, type })}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="تعديل"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete({ type, id: q.id })}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="حذف"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Question text */}
          <div className="flex-1 text-right">
            <RichText
              html={q.question_text}
              dir="rtl"
              className="font-medium text-gray-900 leading-relaxed"
            />
            {q.translation && (
              <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
                {q.translation}
              </p>
            )}
            {q.question_image && (
              <img
                src={q.question_image}
                alt="question"
                className="mt-2 rounded-lg max-h-40 object-contain ml-auto"
              />
            )}
          </div>
        </div>

        {/* Choices */}
        {/* NOTE: choice_a..choice_d come back from the API as HTML,
            so they must go through RichText (dangerouslySetInnerHTML)
            just like question_text — rendering them as plain `{c.value}`
            shows the raw tags on screen instead of formatted text. */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          {choices.map((c) => (
            <div
              key={c.key}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                q.correct_answer === c.key
                  ? "bg-emerald-50 border border-emerald-300"
                  : "bg-gray-50 border border-transparent"
              }`}
            >
              {q.correct_answer === c.key && (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              )}
              <span
                className={`font-semibold flex-shrink-0 ${
                  q.correct_answer === c.key
                    ? "text-emerald-700"
                    : "text-gray-400"
                }`}
              >
                {c.key}.
              </span>
              <RichText
                html={c.value}
                dir="rtl"
                className={`flex-1 [&_p]:m-0 [&_ul]:m-0 [&_ul]:pl-4 [&_ol]:m-0 [&_ol]:pl-4 ${
                  q.correct_answer === c.key
                    ? "text-emerald-800 font-medium"
                    : "text-gray-600"
                }`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Footer: difficulty + toggle explanation */}
      <div className="px-4 pb-3 flex items-center justify-between">
        <button
          onClick={() => setExpanded((p) => !p)}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          {expanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
          {expanded ? "إخفاء الشرح" : "عرض الشرح"}
        </button>
        {q.difficulty && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              DIFFICULTY_COLORS[q.difficulty] || "bg-gray-100 text-gray-500"
            }`}
          >
            {q.difficulty}
          </span>
        )}
      </div>

      {/* Explanation — same issue as choices: explanation / english_explanation
          are HTML strings from the API and must go through RichText. */}
      {expanded && (q.explanation || q.english_explanation) && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-2">
          {q.explanation && (
            <RichText
              html={q.explanation}
              dir="rtl"
              className="text-sm text-gray-600 leading-relaxed bg-amber-50 rounded-lg px-3 py-2 border-r-4 border-amber-400 [&_p]:m-0"
            />
          )}
          {q.english_explanation && (
            <RichText
              html={q.english_explanation}
              dir="ltr"
              className="text-sm text-gray-600 leading-relaxed bg-amber-50 rounded-lg px-3 py-2 border-l-4 border-amber-400 [&_p]:m-0"
            />
          )}
        </div>
      )}
      {expanded && !q.explanation && !q.english_explanation && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-400 text-right italic">
            لا يوجد شرح لهذا السؤال
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Listening Audio Card (with edit)
// ─────────────────────────────────────────────
function ListeningAudioCard({ audio, skillId, onUpdate, navigate, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(audio.audio_file || "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(audio.audio_image || null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [form, setForm] = useState({
    title: audio.title,
    transcript: audio.transcript || "",
    duration: audio.duration || 0,
    difficulty: audio.difficulty || "MEDIUM",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAudioFile(file);
    setAudioPreview(URL.createObjectURL(file));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageRemoved(false);
    }
  };
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };
  const handleSubmit = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("transcript", form.transcript);
      formData.append("duration", form.duration);
      formData.append("difficulty", form.difficulty);
      if (audioFile) formData.append("audio_file", audioFile);
      if (imageFile) {
        formData.append("audio_image", imageFile);
      } else if (imageRemoved) {
        formData.append("audio_image", "");
      }
      await generalQuestionsAPI.updateListeningAudio(audio.id, formData);
      setEditing(false);
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await generalQuestionsAPI.deleteListeningAudio(audio.id);
      onUpdate();
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
              تأكيد الحذف
            </h3>
            <p className="text-gray-500 text-center text-sm mb-6">
              هل أنت متأكد من حذف هذا التسجيل وجميع أسئلته؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setEditing((v) => !v)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="text-right">
            <h4 className="font-bold text-gray-900">{audio.title}</h4>
            <span className="text-xs text-gray-400">
              {audio.duration}s • {audio.difficulty}
            </span>
          </div>
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                العنوان
              </label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                الملف الصوتي
              </label>
              <label className="flex items-center gap-2 cursor-pointer w-full border-2 border-dashed border-cyan-300 rounded-lg px-3 py-3 bg-cyan-50 hover:bg-cyan-100 transition-colors">
                <Headphones className="w-4 h-4 text-cyan-500 shrink-0" />
                <span className="text-xs text-cyan-600 font-medium">
                  {audioFile ? audioFile.name : "اضغط لرفع ملف صوتي"}
                </span>
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleAudioChange}
                />
              </label>
              {audioPreview && (
                <audio controls className="w-full mt-2" src={audioPreview}>
                  المتصفح لا يدعم تشغيل الصوت
                </audio>
              )}
            </div>
            <ImageField
              preview={imagePreview}
              onChange={handleImageChange}
              onRemove={removeImage}
              accentColor="cyan"
            />
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                النص (اختياري)
              </label>
              <textarea
                value={form.transcript}
                onChange={(e) =>
                  setForm({ ...form, transcript: e.target.value })
                }
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  المدة (ثانية)
                </label>
                <input
                  type="number"
                  value={form.duration}
                  onChange={(e) =>
                    setForm({ ...form, duration: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  الصعوبة
                </label>
                <select
                  value={form.difficulty}
                  onChange={(e) =>
                    setForm({ ...form, difficulty: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
                >
                  <option value="EASY">سهل</option>
                  <option value="MEDIUM">متوسط</option>
                  <option value="HARD">صعب</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 rounded-lg border text-xs text-gray-600"
              >
                إلغاء
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-xs flex items-center gap-1"
              >
                {saving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                حفظ
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50">
        {audio.audio_image && (
          <img
            src={audio.audio_image}
            alt="Thumbnail"
            className="w-full max-h-40 object-contain rounded-lg border border-gray-200 mb-3"
          />
        )}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() =>
              navigate(
                `/dashboard/general/skills/${skillId}/add/listening/audio/${audio.id}/questions`
              )
            }
            className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-semibold"
          >
            <Plus className="w-4 h-4" />
            إضافة سؤال
          </button>
          <span className="text-xs text-gray-400 font-medium">
            {audio.questions?.length || 0} سؤال
          </span>
        </div>
        {audio.questions?.length > 0 && (
          <div className="space-y-3">
            {audio.questions.map((q) => (
              <MCQCard
                key={q.id}
                q={q}
                type="LISTENING"
                onDelete={() => onUpdate()}
                onEdit={onEdit}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Speaking Video Card (with edit)
// ─────────────────────────────────────────────
function SpeakingVideoCard({ video, skillId, onUpdate, navigate, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(video.video_file || "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(video.video_image || null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [form, setForm] = useState({
    title: video.title,
    description: video.description || "",
    duration: video.duration || 0,
    difficulty: video.difficulty || "MEDIUM",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageRemoved(false);
    }
  };
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("description", form.description);
      formData.append("duration", form.duration);
      formData.append("difficulty", form.difficulty);
      if (videoFile) formData.append("video_file", videoFile);
      if (imageFile) {
        formData.append("video_image", imageFile);
      } else if (imageRemoved) {
        formData.append("video_image", "");
      }
      await generalQuestionsAPI.updateSpeakingVideo(video.id, formData);
      setEditing(false);
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await generalQuestionsAPI.deleteSpeakingVideo(video.id);
      onUpdate();
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
              تأكيد الحذف
            </h3>
            <p className="text-gray-500 text-center text-sm mb-6">
              هل أنت متأكد من حذف هذا الفيديو وجميع أسئلته؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setEditing((v) => !v)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="text-right">
            <h4 className="font-bold text-gray-900">{video.title}</h4>
            <span className="text-xs text-gray-400">
              {video.duration}s • {video.difficulty}
            </span>
          </div>
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                العنوان
              </label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                ملف الفيديو
              </label>
              <label className="flex items-center gap-2 cursor-pointer w-full border-2 border-dashed border-rose-300 rounded-lg px-3 py-3 bg-rose-50 hover:bg-rose-100 transition-colors">
                <Mic className="w-4 h-4 text-rose-500 shrink-0" />
                <span className="text-xs text-rose-600 font-medium">
                  {videoFile ? videoFile.name : "اضغط لرفع فيديو"}
                </span>
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={handleVideoChange}
                />
              </label>
              {videoPreview && (
                <video
                  controls
                  className="w-full mt-2 rounded-lg"
                  src={videoPreview}
                >
                  المتصفح لا يدعم تشغيل الفيديو
                </video>
              )}
            </div>
            <ImageField
              preview={imagePreview}
              onChange={handleImageChange}
              onRemove={removeImage}
              accentColor="rose"
            />
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                الوصف (اختياري)
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  المدة (ثانية)
                </label>
                <input
                  type="number"
                  value={form.duration}
                  onChange={(e) =>
                    setForm({ ...form, duration: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  الصعوبة
                </label>
                <select
                  value={form.difficulty}
                  onChange={(e) =>
                    setForm({ ...form, difficulty: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                >
                  <option value="EASY">سهل</option>
                  <option value="MEDIUM">متوسط</option>
                  <option value="HARD">صعب</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 rounded-lg border text-xs text-gray-600"
              >
                إلغاء
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs flex items-center gap-1"
              >
                {saving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                حفظ
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50">
        {video.video_image && (
          <img
            src={video.video_image}
            alt="Thumbnail"
            className="w-full max-h-40 object-contain rounded-lg border border-gray-200 mb-3"
          />
        )}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() =>
              navigate(
                `/dashboard/general/skills/${skillId}/add/speaking/video/${video.id}/questions`
              )
            }
            className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-semibold"
          >
            <Plus className="w-4 h-4" />
            إضافة سؤال
          </button>
          <span className="text-xs text-gray-400 font-medium">
            {video.questions?.length || 0} سؤال
          </span>
        </div>
        {video.questions?.length > 0 && (
          <div className="space-y-3">
            {video.questions.map((q) => (
              <MCQCard
                key={q.id}
                q={q}
                type="SPEAKING"
                onDelete={() => onUpdate()}
                onEdit={onEdit}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Edit MCQ Modal
// ─────────────────────────────────────────────
function EditMCQModal({ editTarget, isGeneralPath, allQuestions, onClose, onSaved }) {
  const { q, type } = editTarget;

  const initialOptions = [
    q.choice_a,
    q.choice_b,
    q.choice_c,
    q.choice_d,
  ].filter(Boolean);

  const [form, setForm] = useState({
    question_text: q.question_text || "",
    explanation: q.explanation || "",
    english_explanation: q.english_explanation || "",
    translation: q.translation || "",
    difficulty: q.difficulty || "MEDIUM",
    options: initialOptions.length >= 2 ? initialOptions : ["", "", "", ""],
    correct_answer_index: ["A", "B", "C", "D"].indexOf(q.correct_answer),
  });
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(q.question_image || null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [error, setError] = useState("");

  // ─── تحويل نوع السؤال (متاح بس لو السكيل GENERAL_PATH) ───
  const [targetType, setTargetType] = useState(type);
  const isConverting = isGeneralPath && targetType !== type;

  const [parentMode, setParentMode] = useState("existing"); // "existing" | "new"
  const [targetParentId, setTargetParentId] = useState("");
  const [newParentTitle, setNewParentTitle] = useState("");
  const [newPassageText, setNewPassageText] = useState("");
  const [newPassageTranslation, setNewPassageTranslation] = useState("");
  const [newParentFile, setNewParentFile] = useState(null);
  const [newParentTranscript, setNewParentTranscript] = useState("");
  const [newParentDescription, setNewParentDescription] = useState("");
  const [newParentDuration, setNewParentDuration] = useState(0);

  // كل عناصر النوع المستهدف اللي موجودة أصلاً في نفس السكيل (لو محتاج parent)
  const availableParents = PARENT_REQUIRED_TYPES.includes(targetType)
    ? (allQuestions || []).filter((item) => item.type === targetType)
    : [];

  // لما تغيّري النوع المستهدف، صفّري بيانات الـ parent القديمة
  useEffect(() => {
    setParentMode("existing");
    setTargetParentId("");
    setNewParentTitle("");
    setNewPassageText("");
    setNewParentFile(null);
    setNewParentTranscript("");
    setNewParentDescription("");
    setNewParentDuration(0);
  }, [targetType]);

  const updateOption = (i, val) => {
    setForm((f) => {
      const opts = [...f.options];
      opts[i] = val;
      return { ...f, options: opts };
    });
  };
  const handleQuestionTextChange = (html) => {
    setForm((f) => ({ ...f, question_text: html }));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageRemoved(false);
    }
  };
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };

  const handleSave = async () => {
    // question_text comes from the rich text editor as HTML,
    // so an empty value looks like "<p></p>" instead of ""
    if (isHtmlEmpty(form.question_text)) {
      setError("نص السؤال مطلوب");
      return;
    }
    if (form.options.filter((o) => o.trim()).length < 2) {
      setError("أدخل خيارين على الأقل");
      return;
    }
    if (form.correct_answer_index < 0) {
      setError("اختر الإجابة الصحيحة");
      return;
    }

    // ─── فاليديشن إضافي لو بنحول نوع السؤال ───
    if (isConverting && PARENT_REQUIRED_TYPES.includes(targetType)) {
      if (parentMode === "existing" && !targetParentId) {
        setError("اختار عنصر موجود أو حوّلي لـ \"إنشاء جديد\"");
        return;
      }
      if (parentMode === "new") {
        if (!newParentTitle.trim()) {
          setError("العنوان مطلوب");
          return;
        }
        if (targetType === "READING" && !newPassageText.trim()) {
          setError("نص القطعة مطلوب");
          return;
        }
        if (
          (targetType === "LISTENING" || targetType === "SPEAKING") &&
          !newParentFile
        ) {
          setError(
            targetType === "LISTENING"
              ? "الملف الصوتي مطلوب"
              : "ملف الفيديو مطلوب"
          );
          return;
        }
      }
    }

    setError("");
    setSaving(true);

    const formData = new FormData();
    formData.append("question_text", form.question_text);
    formData.append(
      "options",
      JSON.stringify(form.options.filter((o) => o.trim()))
    );
    formData.append("correct_answer", form.options[form.correct_answer_index]);
    formData.append("explanation", form.explanation);
    formData.append("english_explanation", form.english_explanation);
    formData.append("translation", form.translation || "");
    formData.append("difficulty", form.difficulty);
    if (imageFile) {
      formData.append("question_image", imageFile);
    } else if (imageRemoved) {
      formData.append("question_image", "");
    }

    try {
      if (isConverting) {
        // ─── تحويل نوع السؤال لنوع تاني ───
        formData.append("target_type", targetType);

        if (PARENT_REQUIRED_TYPES.includes(targetType)) {
          if (parentMode === "existing") {
            const key =
              targetType === "READING"
                ? "target_passage_id"
                : targetType === "LISTENING"
                ? "target_audio_id"
                : "target_video_id";
            formData.append(key, targetParentId);
          } else {
            if (targetType === "READING") {
              formData.append("new_passage_title", newParentTitle);
              formData.append("new_passage_text", newPassageText);
              formData.append("new_passage_translation", newPassageTranslation || "");
            } else if (targetType === "LISTENING") {
              formData.append("new_audio_title", newParentTitle);
              formData.append("audio_file", newParentFile);
              formData.append("new_audio_transcript", newParentTranscript);
              formData.append("new_audio_duration", newParentDuration || 0);
            } else if (targetType === "SPEAKING") {
              formData.append("new_video_title", newParentTitle);
              formData.append("video_file", newParentFile);
              formData.append(
                "new_video_description",
                newParentDescription
              );
              formData.append("new_video_duration", newParentDuration || 0);
            }
          }
        }

        await generalQuestionsAPI.convertQuestionType(type, q.id, formData);
      } else {
        // ─── تعديل عادي من غير تغيير النوع ───
        if (type === "VOCABULARY") {
          await generalQuestionsAPI.updateVocabulary(q.id, formData);
        } else if (type === "GRAMMAR") {
          await generalQuestionsAPI.updateGrammar(q.id, formData);
        } else if (type === "READING") {
          await generalQuestionsAPI.updateReadingQuestion(q.id, formData);
        } else if (type === "LISTENING") {
          await generalQuestionsAPI.updateListeningQuestion(q.id, formData);
        } else if (type === "SPEAKING") {
          await generalQuestionsAPI.updateSpeakingQuestion(q.id, formData);
        }
      }
      onSaved();
    } catch (e) {
      setError("حدث خطأ أثناء الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="font-bold text-gray-900 text-base">تعديل السؤال</h2>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* ─── تحويل نوع السؤال (GENERAL_PATH فقط) ─── */}
          {isGeneralPath && (
            <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-3">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-indigo-800 mb-2 justify-end">
                <Repeat className="w-3.5 h-3.5" />
                نوع السؤال
              </label>
              <div className="flex gap-2 flex-wrap justify-end">
                {MCQ_TYPE_OPTIONS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTargetType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      targetType === t
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100"
                    }`}
                  >
                    {TYPE_LABELS_AR[t]}
                  </button>
                ))}
              </div>
              {isConverting && (
                <p className="text-xs text-indigo-700 mt-2 text-right leading-relaxed">
                  هيتم تحويل السؤال من {TYPE_LABELS_AR[type]} لـ{" "}
                  {TYPE_LABELS_AR[targetType]}. محاولات الطلاب السابقة (لو
                  موجودة) هتفضل زي ما هي ومش هتتأثر.
                </p>
              )}
            </div>
          )}

          {/* ─── اختيار/إنشاء Passage/Audio/Video لو التحويل محتاج parent ─── */}
          {isConverting && PARENT_REQUIRED_TYPES.includes(targetType) && (
            <div className="border border-gray-200 rounded-xl p-3 space-y-3">
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setParentMode("existing")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    parentMode === "existing"
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  استخدام موجود
                </button>
                <button
                  type="button"
                  onClick={() => setParentMode("new")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    parentMode === "new"
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  إنشاء جديد
                </button>
              </div>

              {parentMode === "existing" ? (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block text-right">
                    {targetType === "READING"
                      ? "اختر القطعة"
                      : targetType === "LISTENING"
                      ? "اختر التسجيل الصوتي"
                      : "اختر الفيديو"}
                  </label>
                  <select
                    value={targetParentId}
                    onChange={(e) => setTargetParentId(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">-- اختر --</option>
                    {availableParents.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                  {availableParents.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      لا يوجد عناصر موجودة من النوع ده لسه، اختار "إنشاء جديد"
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    value={newParentTitle}
                    onChange={(e) => setNewParentTitle(e.target.value)}
                    placeholder="العنوان"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />

                  {targetType === "READING" && (
                    <><textarea
                      value={newPassageText}
                      onChange={(e) => setNewPassageText(e.target.value)}
                      placeholder="نص القطعة"
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <TranslationField
                      label="ترجمة نص القطعة الجديدة"
                      value={newPassageTranslation}
                      onChange={setNewPassageTranslation}
                    />
                    </>
                  )}

                  {targetType === "LISTENING" && (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer w-full border-2 border-dashed border-cyan-300 rounded-lg px-3 py-3 bg-cyan-50 hover:bg-cyan-100 transition-colors">
                        <Headphones className="w-4 h-4 text-cyan-500 shrink-0" />
                        <span className="text-xs text-cyan-600 font-medium">
                          {newParentFile
                            ? newParentFile.name
                            : "اضغط لرفع ملف صوتي"}
                        </span>
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={(e) =>
                            setNewParentFile(e.target.files[0] || null)
                          }
                        />
                      </label>
                      <textarea
                        value={newParentTranscript}
                        onChange={(e) =>
                          setNewParentTranscript(e.target.value)
                        }
                        placeholder="النص الكتابي (اختياري)"
                        rows={2}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      />
                      <input
                        type="number"
                        value={newParentDuration}
                        onChange={(e) =>
                          setNewParentDuration(e.target.value)
                        }
                        placeholder="المدة بالثواني"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      />
                    </>
                  )}

                  {targetType === "SPEAKING" && (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer w-full border-2 border-dashed border-rose-300 rounded-lg px-3 py-3 bg-rose-50 hover:bg-rose-100 transition-colors">
                        <Video className="w-4 h-4 text-rose-500 shrink-0" />
                        <span className="text-xs text-rose-600 font-medium">
                          {newParentFile
                            ? newParentFile.name
                            : "اضغط لرفع فيديو"}
                        </span>
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={(e) =>
                            setNewParentFile(e.target.files[0] || null)
                          }
                        />
                      </label>
                      <textarea
                        value={newParentDescription}
                        onChange={(e) =>
                          setNewParentDescription(e.target.value)
                        }
                        placeholder="الوصف (اختياري)"
                        rows={2}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-rose-300"
                      />
                      <input
                        type="number"
                        value={newParentDuration}
                        onChange={(e) =>
                          setNewParentDuration(e.target.value)
                        }
                        placeholder="المدة بالثواني"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-rose-300"
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Question text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              نص السؤال
            </label>
            <RichTextEditor
              value={form.question_text}
              onChange={handleQuestionTextChange}
              accentColor="emerald"
              dir="rtl"
            />
            <TranslationField
              label="ترجمة نص السؤال"
              value={form.translation}
              onChange={(v) => setForm((prev) => ({ ...prev, translation: v }))}
            />
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              الخيارات — اختر الإجابة الصحيحة
            </label>
            <div className="space-y-2">
              {form.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    dir="auto"
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                    placeholder={`الخيار ${String.fromCharCode(65 + i)}`}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, correct_answer_index: i }))
                    }
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      form.correct_answer_index === i
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-gray-300 text-gray-300 hover:border-emerald-400"
                    }`}
                  >
                    {form.correct_answer_index === i ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <span className="text-xs font-bold">
                        {String.fromCharCode(65 + i)}
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <ImageField
            preview={imagePreview}
            onChange={handleImageChange}
            onRemove={removeImage}
            accentColor="emerald"
          />
          {/* Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              الصعوبة
            </label>
            <div className="flex gap-2 justify-end">
              {["EASY", "MEDIUM", "HARD"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, difficulty: d }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    form.difficulty === d
                      ? DIFFICULTY_COLORS[d]
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Explanation (Arabic) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              الشرح بالعربي (اختياري)
            </label>
            <textarea
              dir="auto"
              rows={3}
              value={form.explanation}
              onChange={(e) =>
                setForm((f) => ({ ...f, explanation: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              placeholder="اكتب الشرح هنا..."
            />
          </div>

          {/* Explanation (English) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-left">
              Explanation in English (optional)
            </label>
            <textarea
              dir="ltr"
              rows={3}
              value={form.english_explanation}
              onChange={(e) =>
                setForm((f) => ({ ...f, english_explanation: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              placeholder="Write the explanation here..."
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm text-right font-medium">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
          >
            إلغاء
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isConverting ? "تحويل وحفظ" : "حفظ التعديلات"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Pagination bar — shown at the bottom of the list
// ─────────────────────────────────────────────
function EditPassageModal({ passage, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: passage.title || "",
    passage_text: passage.passage_text || "",
    translation: passage.translation || "",
    difficulty: passage.difficulty || "MEDIUM",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(passage.passage_image || null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageRemoved(false);
    }
  };
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageRemoved(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.passage_text.trim()) {
      setError("العنوان ونص القطعة مطلوبين");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("passage_text", form.passage_text);
      fd.append("translation", form.translation || "");
      fd.append("difficulty", form.difficulty);
      if (imageFile) fd.append("passage_image", imageFile);
      else if (imageRemoved) fd.append("passage_image", "");
      await generalQuestionsAPI.updateReadingPassage(passage.id, fd);
      onSaved();
    } catch (e) {
      setError("حدث خطأ أثناء الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="font-bold text-gray-900 text-base">تعديل القطعة</h2>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              العنوان
            </label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <ImageField
            preview={imagePreview}
            onChange={handleImageChange}
            onRemove={removeImage}
            accentColor="orange"
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              نص القطعة
            </label>
            <textarea
              rows={6}
              value={form.passage_text}
              onChange={(e) =>
                setForm((f) => ({ ...f, passage_text: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none leading-relaxed"
            />
            <TranslationField
              label="ترجمة نص القطعة"
              value={form.translation}
              onChange={(v) => setForm((f) => ({ ...f, translation: v }))}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 text-right">
              الصعوبة
            </label>
            <div className="flex gap-2 justify-end">
              {["EASY", "MEDIUM", "HARD"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, difficulty: d }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    form.difficulty === d
                      ? DIFFICULTY_COLORS[d]
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm text-right font-medium">{error}</p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-sm"
          >
            إلغاء
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            حفظ التعديلات
          </button>
        </div>
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 pt-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        السابق
      </button>

      <span className="text-sm text-gray-500 px-2">
        صفحة {page} من {totalPages}
      </span>

      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        التالي
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// General Path Section Component
// ─────────────────────────────────────────────
function GeneralPathSection({
  section,
  questions,
  skillId,
  navigate,
  onUpdate,
  onEdit,
  onDelete,
}) {
  const [expanded, setExpanded] = useState(true);
  const Icon = section.icon;
  const sectionQuestions = questions.filter((q) => q.type === section.type);

  const renderSectionContent = () => {
    if (section.type === "VOCABULARY" || section.type === "GRAMMAR") {
      return sectionQuestions.map((q) => (
        <MCQCard
          key={q.id}
          q={q}
          type={section.type}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      ));
    }

    if (section.type === "READING") {
      return sectionQuestions.map((passage) => (
        <div
          key={passage.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <button
                onClick={() =>
                  onDelete({
                    type: "READING",
                    id: passage.id,
                    parentType: "passage",
                  })
                }
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="text-right">
                <h4 className="font-bold text-gray-900">{passage.title}</h4>
                <button
                  type="button"
                  onClick={() => onEdit({ passage, type: "READING_PASSAGE" })}
                  className="mt-1 mb-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  <Pencil className="w-3 h-3" />
                  تعديل القطعة / الصورة
                </button>
                {passage.difficulty && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      DIFFICULTY_COLORS[passage.difficulty] ||
                      "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {passage.difficulty}
                  </span>
                )}
              </div>
            </div>
            {passage.passage_image && (
              <img
                src={passage.passage_image}
                alt="passage"
                className="mb-3 rounded-lg max-h-56 w-full object-contain border border-gray-100"
              />
            )}
            <p
              dir="rtl"
              className="text-sm text-gray-600 text-right leading-relaxed border-r-4 border-amber-300 pr-3"
            >
              {passage.passage_text}
            </p>
            {passage.translation && (
              <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
                {passage.translation}
              </p>
            )}
          </div>
          <div className="p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() =>
                  navigate(
                    `/dashboard/general/skills/${skillId}/add/reading/passage/${passage.id}/questions`
                  )
                }
                className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                <Plus className="w-4 h-4" />
                إضافة سؤال
              </button>
              <span className="text-xs text-gray-400 font-medium">
                {passage.questions?.length || 0} سؤال
              </span>
            </div>
            {passage.questions?.length > 0 && (
              <div className="space-y-3">
                {passage.questions.map((q) => (
                  <MCQCard
                    key={q.id}
                    q={q}
                    type="READING"
                    onDelete={onDelete}
                    onEdit={onEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ));
    }

    if (section.type === "LISTENING") {
      return sectionQuestions.map((audio) => (
        <ListeningAudioCard
          key={audio.id}
          audio={audio}
          skillId={skillId}
          onUpdate={onUpdate}
          navigate={navigate}
          onEdit={onEdit}
        />
      ));
    }

    if (section.type === "SPEAKING") {
      return sectionQuestions.map((video) => (
        <SpeakingVideoCard
          key={video.id}
          video={video}
          skillId={skillId}
          onUpdate={onUpdate}
          navigate={navigate}
          onEdit={onEdit}
        />
      ));
    }

    return null;
  };

  return (
    <div className={`border ${section.border} rounded-xl overflow-hidden`}>
      {/* Section Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${section.headerBg}`}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <div className={`p-1.5 rounded-lg ${section.bg}`}>
            <Icon className={`w-4 h-4 ${section.color}`} />
          </div>
          <span className={`font-semibold text-sm ${section.color}`}>
            {section.label}
          </span>
          <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded-full border">
            {sectionQuestions.length} items
          </span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(section.addRoute(skillId))}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-white transition-colors ${section.btnColor}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {section.addLabel}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className={`p-1 ${section.color}`}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Section Content */}
      {expanded && (
        <div className="p-4 space-y-3 bg-white">
          {sectionQuestions.length === 0 ? (
            <div className="text-center py-8">
              <Icon
                className={`w-8 h-8 ${section.color} mx-auto mb-2 opacity-25`}
              />
              <p className="text-gray-400 text-sm">
                No {section.label} questions yet
              </p>
              <button
                onClick={() => navigate(section.addRoute(skillId))}
                className={`inline-flex items-center gap-1.5 mt-3 text-xs font-medium ${section.color} hover:underline`}
              >
                <Plus className="w-3.5 h-3.5" />
                Start adding {section.label}
              </button>
            </div>
          ) : (
            renderSectionContent()
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
export default function GeneralSkillDetails() {
  const navigate = useNavigate();
  const { skillId } = useParams();

  const [skill, setSkill] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // ✅ Pagination state (للـ skills العادية بس، GENERAL_PATH بتجيب لحد 500 سؤال دفعة واحدة)
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchData();
  }, [skillId, page]);

  // لما نغيّر السكيل، نرجّع الصفحة لـ 1
  useEffect(() => {
    setPage(1);
  }, [skillId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // ✅ جيب skill data الأول
      const skillData = await generalSkillsAPI.getById(skillId);
      setSkill(skillData);

      // ✅ دلوقتي تعرف الـ pageSize صح
      const isGeneralPath = skillData?.skill_type === "GENERAL_PATH";
      const pageSize = isGeneralPath ? 500 : PAGE_SIZE;
      const currentPage = isGeneralPath ? 1 : page;

      const qData = await generalQuestionsAPI.getSkillQuestions(
        skillId,
        currentPage,
        pageSize
      );
      setQuestions(qData.questions || []);

      // ✅ استخرج عدد الصفحات — الـ API بيرجعها جوه object اسمه "pagination"
      if (!isGeneralPath) {
        const total =
          qData.pagination?.total_items ??
          qData.total_count ??
          qData.count ??
          qData.total ??
          0;
        const pages =
          qData.pagination?.total_pages ??
          qData.total_pages ??
          (total ? Math.ceil(total / pageSize) : 1);
        setTotalPages(pages || 1);
      } else {
        setTotalPages(1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAddPath = () => {
    const type = skill?.skill_type;
    if (type === "VOCABULARY")
      return `/dashboard/general/skills/${skillId}/add/vocabulary`;
    if (type === "GRAMMAR")
      return `/dashboard/general/skills/${skillId}/add/grammar`;
    if (type === "READING")
      return `/dashboard/general/skills/${skillId}/add/reading/passage`;
    if (type === "LISTENING")
      return `/dashboard/general/skills/${skillId}/add/listening/audio`;
    if (type === "SPEAKING")
      return `/dashboard/general/skills/${skillId}/add/speaking/video`;
    if (type === "WRITING")
      return `/dashboard/general/skills/${skillId}/add/writing`;
    return "#";
  };

  const handleDeleteQuestion = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      const { type, id, parentType } = deleteModal;
      if (type === "VOCABULARY") await generalQuestionsAPI.deleteVocabulary(id);
      else if (type === "GRAMMAR") await generalQuestionsAPI.deleteGrammar(id);
      else if (type === "READING" && parentType === "passage")
        await generalQuestionsAPI.deleteReadingPassage(id);
      else if (type === "READING")
        await generalQuestionsAPI.deleteReadingQuestion(id);
      else if (type === "LISTENING" && parentType === "audio")
        await generalQuestionsAPI.deleteListeningAudio(id);
      else if (type === "LISTENING")
        await generalQuestionsAPI.deleteListeningQuestion(id);
      else if (type === "SPEAKING" && parentType === "video")
        await generalQuestionsAPI.deleteSpeakingVideo(id);
      else if (type === "SPEAKING")
        await generalQuestionsAPI.deleteSpeakingQuestion(id);
      else if (type === "WRITING") await generalQuestionsAPI.deleteWriting(id);
      setDeleteModal(null);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !skill) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const isGeneralPath = skill?.skill_type === "GENERAL_PATH";
  const SkillIcon = SKILL_ICONS[skill?.skill_type] || BookOpen;

  // ── Render helpers ──────────────────────────
  const renderQuestions = () => {
    const type = skill?.skill_type;

    if (type === "VOCABULARY" || type === "GRAMMAR") {
      return questions.map((q) => (
        <MCQCard
          key={q.id}
          q={q}
          type={type}
          onDelete={setDeleteModal}
          onEdit={setEditTarget}
        />
      ));
    }

    if (type === "READING") {
      return questions.map((passage) => (
        <div
          key={passage.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <button
                onClick={() =>
                  setDeleteModal({
                    type: "READING",
                    id: passage.id,
                    parentType: "passage",
                  })
                }
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="text-right">
                <h4 className="font-bold text-gray-900">{passage.title}</h4>
                <button
                  type="button"
                  onClick={() => setEditTarget({ passage, type: "READING_PASSAGE" })}
                  className="mt-1 mb-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  <Pencil className="w-3 h-3" />
                  تعديل القطعة / الصورة
                </button>
                {passage.difficulty && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      DIFFICULTY_COLORS[passage.difficulty] ||
                      "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {passage.difficulty}
                  </span>
                )}
              </div>
            </div>
            {passage.passage_image && (
              <img
                src={passage.passage_image}
                alt="passage"
                className="mb-3 rounded-lg max-h-56 w-full object-contain border border-gray-100"
              />
            )}
            <p
              dir="rtl"
              className="text-sm text-gray-600 text-right leading-relaxed border-r-4 border-amber-300 pr-3"
            >
              {passage.passage_text}
            </p>
            {passage.translation && (
              <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
                {passage.translation}
              </p>
            )}
          </div>

          <div className="p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() =>
                  navigate(
                    `/dashboard/general/skills/${skillId}/add/reading/passage/${passage.id}/questions`
                  )
                }
                className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                <Plus className="w-4 h-4" />
                إضافة سؤال
              </button>
              <span className="text-xs text-gray-400 font-medium">
                {passage.questions?.length || 0} سؤال
              </span>
            </div>

            {passage.questions?.length > 0 && (
              <div className="space-y-3">
                {passage.questions.map((q) => (
                  <MCQCard
                    key={q.id}
                    q={q}
                    type="READING"
                    onDelete={setDeleteModal}
                    onEdit={setEditTarget}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ));
    }

    if (type === "LISTENING") {
      return questions.map((audio) => (
        <ListeningAudioCard
          key={audio.id}
          audio={audio}
          skillId={skillId}
          onUpdate={fetchData}
          navigate={navigate}
          onEdit={setEditTarget}
        />
      ));
    }

    if (type === "SPEAKING") {
      return questions.map((video) => (
        <SpeakingVideoCard
          key={video.id}
          video={video}
          skillId={skillId}
          onUpdate={fetchData}
          navigate={navigate}
          onEdit={setEditTarget}
        />
      ));
    }

    if (type === "WRITING") {
      return questions.map((q) => (
        <div
          key={q.id}
          className="bg-white rounded-xl border border-gray-200 p-4"
        >
          <div className="flex items-start gap-3">
            <button
              onClick={() => setDeleteModal({ type: "WRITING", id: q.id })}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <div className="text-right flex-1">
              <h4 className="font-bold text-gray-900 mb-1">{q.title}</h4>
              <RichText
                html={q.question_text}
                dir="rtl"
                className="text-sm text-gray-600 line-clamp-2"
              />
              {q.translation && (
                <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
                  {q.translation}
                </p>
              )}
              {q.question_image && (
                <img
                  src={q.question_image}
                  alt="question"
                  className="mt-2 rounded-lg max-h-40 object-contain ml-auto"
                />
              )}
              <div className="flex gap-2 mt-2 justify-end flex-wrap">
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                  {q.min_words} - {q.max_words} كلمة
                </span>
                {q.difficulty && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      DIFFICULTY_COLORS[q.difficulty] ||
                      "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {q.difficulty}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ));
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              navigate(
                `/dashboard/general/categories/${skill?.category}/skills`
              )
            }
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isGeneralPath ? "bg-indigo-100" : "bg-emerald-100"
            }`}
          >
            <SkillIcon
              className={`w-5 h-5 ${
                isGeneralPath ? "text-indigo-700" : "text-emerald-700"
              }`}
            />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{skill?.title}</h1>
            <p className="text-sm text-gray-500">
              {SKILL_TYPE_LABELS[skill?.skill_type]}
              {!isGeneralPath && ` • ${questions.length} عنصر`}
            </p>
          </div>
        </div>
        {/* Only show add button for non-general-path skills */}
        {!isGeneralPath && (
          <button
            onClick={() => navigate(getAddPath())}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            إضافة{" "}
            {skill?.skill_type === "READING"
              ? "قطعة"
              : skill?.skill_type === "LISTENING"
              ? "تسجيل صوتي"
              : skill?.skill_type === "SPEAKING"
              ? "فيديو"
              : "سؤال"}
          </button>
        )}
      </div>

      {/* GENERAL PATH — Sections */}
      {isGeneralPath ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <GitBranch className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-900">
              General Path Content
            </h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              Add questions directly in each section
            </span>
          </div>
          {GENERAL_PATH_SECTIONS.map((section) => (
            <GeneralPathSection
              key={section.type}
              section={section}
              questions={questions}
              skillId={skillId}
              navigate={navigate}
              onUpdate={fetchData}
              onEdit={setEditTarget}
              onDelete={setDeleteModal}
            />
          ))}
        </div>
      ) : (
        /* Normal Skills */
        <>
          {questions.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <SkillIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>لا توجد أسئلة بعد</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">{renderQuestions()}</div>
              {/* ✅ Pagination controls */}
              <PaginationBar
                page={page}
                totalPages={totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= totalPages) setPage(p);
                }}
              />
            </>
          )}
        </>
      )}

      {editTarget && editTarget.type === "READING_PASSAGE" && (
        <EditPassageModal
          passage={editTarget.passage}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            fetchData();
          }}
        />
      )}

      {/* Edit Modal */}
      {editTarget && editTarget.type !== "READING_PASSAGE" && (
        <EditMCQModal
          editTarget={editTarget}
          isGeneralPath={isGeneralPath}
          allQuestions={questions}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            fetchData();
          }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
              تأكيد الحذف
            </h3>
            <p className="text-gray-500 text-center text-sm mb-6">
              هل أنت متأكد من حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteQuestion}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}